/**
 * "Log their reply": paste what the person sent on WhatsApp and save it with
 * one tap. It is recorded as an inbound WhatsApp message on the person, and on
 * the lead when opened from lead detail.
 *
 * A failed save keeps the dialog open with the text in it, and the dialog
 * cannot be closed while a save is in flight. A saved reply is followed by
 * "When should you follow up next?".
 */

import { Loader2, Save } from "lucide-react";
import { useCallback, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { useLogContactMessage } from "@/features/realtor/hooks/use-realtor-crm";
import { errorMessage } from "@/features/realtor/utils/error-message";
import { StudioButton } from "@/features/studio/theme";
import {
	NextStepPrompt,
	type NextStepTarget,
} from "../follow-ups/next-step-prompt";

/** The server rejects a longer body. */
const MAX_BODY = 5000;

export function LogReplyDialog({
	open,
	onOpenChange,
	contactId,
	contactName,
	leadId,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	contactId: string;
	contactName: string;
	/** The lead the reply is about, when logged from lead detail. */
	leadId?: string;
}) {
	const { t } = useTranslation();
	const ids = useId();
	const logMessage = useLogContactMessage();
	const [body, setBody] = useState("");
	const [nextStep, setNextStep] = useState<NextStepTarget | null>(null);
	const closeNextStep = useCallback(() => setNextStep(null), []);
	const text = body.trim();
	const busy = logMessage.isPending;

	const close = (next: boolean) => {
		// Closing mid-save would clear the text a failed save has to keep.
		if (busy && !next) return;
		if (!next) setBody("");
		onOpenChange(next);
	};

	const save = async () => {
		if (!text || busy) return;
		try {
			await logMessage.mutateAsync({
				contactId,
				input: {
					direction: "inbound",
					channel: "whatsapp",
					body: text,
					...(leadId ? { leadId } : {}),
				},
			});
			toast.success(t("realtor.timeline.logReply.saved"));
			setBody("");
			onOpenChange(false);
			setNextStep({
				contactId,
				contactName,
				fromLeadId: leadId ?? null,
			});
		} catch (error: unknown) {
			toast.error(errorMessage(error, t("realtor.timeline.logReply.failed")));
		}
	};

	return (
		<>
			<Dialog open={open} onOpenChange={close}>
				<DialogContent className="border-dashboard-border bg-dashboard-bg">
					<DialogHeader>
						<DialogTitle className="font-display text-xl italic font-medium text-dashboard-text">
							{t("realtor.timeline.logReply.title")}
						</DialogTitle>
						<DialogDescription className="text-sm text-dashboard-muted">
							{t("realtor.timeline.logReply.description", {
								name: contactName,
							})}
						</DialogDescription>
					</DialogHeader>
					<form
						id={`${ids}-form`}
						onSubmit={(event) => {
							event.preventDefault();
							void save();
						}}
					>
						<label htmlFor={`${ids}-body`} className="sr-only">
							{t("realtor.timeline.logReply.label")}
						</label>
						<textarea
							id={`${ids}-body`}
							value={body}
							onChange={(event) => setBody(event.target.value)}
							maxLength={MAX_BODY}
							rows={5}
							placeholder={t("realtor.timeline.logReply.placeholder")}
							className="w-full resize-y rounded-lg border border-dashboard-border bg-dashboard-paper px-3 py-2 text-sm text-dashboard-text placeholder:text-dashboard-muted focus:outline-none focus:ring-2 focus:ring-dashboard-accent/40"
						/>
					</form>
					<DialogFooter>
						<StudioButton
							variant="secondary"
							disabled={busy}
							onClick={() => close(false)}
						>
							{t("realtor.timeline.logReply.cancel")}
						</StudioButton>
						<StudioButton
							type="submit"
							form={`${ids}-form`}
							icon={busy ? undefined : Save}
							disabled={!text || busy}
						>
							{busy && <Loader2 className="h-4 w-4 animate-spin" />}
							{busy
								? t("realtor.timeline.logReply.saving")
								: t("realtor.timeline.logReply.save")}
						</StudioButton>
					</DialogFooter>
				</DialogContent>
			</Dialog>
			<NextStepPrompt target={nextStep} onClose={closeNextStep} />
		</>
	);
}
